export const CAST_IN_TYPES = ["High-top Cast-in", "Low-top Cast-in", "Ultra-low Cast-in"]

export interface BestMatchRow {
  element_name?: string | null
  application_name?: string | null
  service_name?: string | null
  nominal_size?: string | null
  fitting_allowed?: string | null
  frl?: string | null
  product_type?: string | null
  product_selling_price?: number | string | null
}

const extractNumeric = (value: unknown): number => {
  const stripped = String(value ?? "").replace(/[^0-9.]/g, "")
  const parsed = parseFloat(stripped)
  return isNaN(parsed) ? 0 : parsed
}

const isCastIn = (row: BestMatchRow): boolean => CAST_IN_TYPES.includes(String(row.product_type ?? ""))

const buildGroupKey = (row: BestMatchRow): string =>
  [row.element_name, row.application_name, row.service_name, extractNumeric(row.nominal_size), row.fitting_allowed, row.frl].join("|")

// Keeps the first-encountered row on a price tie.
const cheapestOf = <T extends BestMatchRow>(rows: T[]): T | undefined =>
  rows.reduce<T | undefined>((best, current) => {
    if (!best) return current
    return extractNumeric(current.product_selling_price) < extractNumeric(best.product_selling_price) ? current : best
  }, undefined)

// "View best match systems" - collapses rows that are identical in every
// field except FRL/product/price down to the cheapest non-Cast-in match plus
// the cheapest match per Cast-in type, per group.
export const applyBestMatchFilter = <T extends BestMatchRow>(rows: T[] | null | undefined): T[] => {
  if (!rows?.length) return []

  const groups = new Map<string, T[]>()
  for (const row of rows) {
    const key = buildGroupKey(row)
    const existing = groups.get(key)
    if (existing) {
      existing.push(row)
    } else {
      groups.set(key, [row])
    }
  }

  const bestRows: T[] = []
  groups.forEach(groupRows => {
    const nonCastIn = groupRows.filter(row => !isCastIn(row))
    const castIn = groupRows.filter(isCastIn)

    const bestNonCastIn = cheapestOf(nonCastIn)
    if (bestNonCastIn) bestRows.push(bestNonCastIn)

    CAST_IN_TYPES.forEach(castInType => {
      const bestOfType = cheapestOf(castIn.filter(row => row.product_type === castInType))
      if (bestOfType) bestRows.push(bestOfType)
    })
  })

  return bestRows.sort((a, b) => {
    const sizeDiff = extractNumeric(a.nominal_size) - extractNumeric(b.nominal_size)
    if (sizeDiff !== 0) return sizeDiff

    const aCastInIndex = CAST_IN_TYPES.indexOf(String(a.product_type ?? ""))
    const bCastInIndex = CAST_IN_TYPES.indexOf(String(b.product_type ?? ""))

    if (aCastInIndex === -1 && bCastInIndex === -1) return 0
    if (aCastInIndex === -1) return -1
    if (bCastInIndex === -1) return 1
    return String(a.product_type).localeCompare(String(b.product_type))
  })
}
